import type {
  FetchAttendanceHistoryParams,
  FetchAvailableCollaboratorsParams,
  FetchHrAnalyticsParams,
  FetchHrDashboardParams,
  FetchHrRemoteStorePeriodMetricsParams,
  FetchHrSalesPeaksParams,
  FetchHrStoreWeeklyMetricsParams,
  FetchHrUnavailabilityCalendarParams,
  FetchTransportSegmentsParams,
  FetchWorkShiftsParams,
} from './hr.queries'
import type {
  FetchDistinctPlatformStockLotsParams,
  FetchDistinctPlatformStockProductsParams,
  FetchPlatformStocksParams,
} from './platform-stocks.queries'
import type {
  FetchSmsTrackingListParams,
  FetchSmsTrackingStatsParams,
} from './sms-tracking.queries'

export const keys = {
  featureFlags: {
    all: ['feature-flags'] as const,
    list: () => [...keys.featureFlags.all, 'list'] as const,
  },
  stores: {
    all: ['stores'] as const,
    list: () => [...keys.stores.all, 'list'] as const,
    byId: (id: number | undefined) => [...keys.stores.all, id] as const,
    cameraConfig: {
      summary: (storeId: number | undefined) =>
        [...keys.stores.all, 'camera-config', 'summary', storeId] as const,
      viewSession: (storeId: number | undefined) =>
        [...keys.stores.all, 'camera-config', 'view-session', storeId] as const,
      cameras: {
        list: (storeId: number | undefined) =>
          [...keys.stores.all, 'camera-config', 'cameras', 'list', storeId] as const,
        byId: (storeId: number | undefined, cameraId: number | undefined) =>
          [...keys.stores.all, 'camera-config', 'cameras', 'by-id', storeId, cameraId] as const,
      },
    },
    cameraStream: (storeId: number | undefined) =>
      [...keys.stores.all, 'camera-stream', storeId] as const,
    cameraIds: (storeId: number | undefined) =>
      [...keys.stores.all, 'camera-ids', storeId] as const,
  },
  rbac: {
    all: ['rbac'] as const,
    permissions: {
      all: () => [...keys.rbac.all, 'permissions'] as const,
      current: () => [...keys.rbac.permissions.all(), 'current'] as const,
    },
    roles: {
      all: () => [...keys.rbac.all, 'roles'] as const,
      list: () => [...keys.rbac.roles.all(), 'list'] as const,
      byId: (id: number | undefined) => [...keys.rbac.roles.all(), id] as const,
    },
  },
  hr: {
    all: ['hr'] as const,
    analytics: (params?: FetchHrAnalyticsParams) => [...keys.hr.all, 'analytics', params] as const,
    salesPeaks: (params?: FetchHrSalesPeaksParams) =>
      [...keys.hr.all, 'sales-peaks', params] as const,
    storeWeeklyMetrics: (params: FetchHrStoreWeeklyMetricsParams | undefined) =>
      [...keys.hr.all, 'store-weekly-metrics', params] as const,
    remoteStorePeriodMetrics: (params: FetchHrRemoteStorePeriodMetricsParams | undefined) =>
      [...keys.hr.all, 'remote-store-period-metrics', params] as const,
    dashboard: {
      all: () => [...keys.hr.all, 'dashboard'] as const,
      overview: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.all(), 'overview', params] as const,
      trends: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.all(), 'trends', params] as const,
      coverage: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.all(), 'coverage', params] as const,
      planningStatus: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.all(), 'plannings-status', params] as const,
      effectifDetail: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.all(), 'effectif-detail', params] as const,
      fraisDetail: (params: FetchHrDashboardParams | undefined) =>
        [...keys.hr.dashboard.all(), 'frais-detail', params] as const,
      availableStores: () => [...keys.hr.dashboard.all(), 'available-stores'] as const,
    },
    absenceCalendar: {
      all: () => [...keys.hr.all, 'absence-calendar'] as const,
      list: (params: FetchHrUnavailabilityCalendarParams | undefined) =>
        [...keys.hr.absenceCalendar.all(), 'list', params] as const,
    },
    collaborators: {
      all: () => [...keys.hr.all, 'collaborators'] as const,
      list: (storeId?: number) => [...keys.hr.collaborators.all(), 'list', storeId] as const,
      available: (params?: FetchAvailableCollaboratorsParams) =>
        [...keys.hr.collaborators.all(), 'available', params] as const,
      byId: (id: number | undefined) => [...keys.hr.collaborators.all(), id] as const,
      workTimes: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'work-times'] as const,
      unavailabilities: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'unavailabilities'] as const,
      equipment: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'equipment'] as const,
      clothing: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'clothing'] as const,
      contracts: (collaboratorId: number | undefined) =>
        [...keys.hr.collaborators.byId(collaboratorId), 'contracts'] as const,
    },
    interimAgencies: {
      all: () => [...keys.hr.all, 'interim-agencies'] as const,
      list: (includeArchived?: boolean) =>
        [...keys.hr.interimAgencies.all(), 'list', includeArchived] as const,
    },
    attendanceHistory: {
      all: () => [...keys.hr.all, 'attendance-history'] as const,
      list: (params: FetchAttendanceHistoryParams | undefined) =>
        [...keys.hr.attendanceHistory.all(), 'list', params] as const,
    },
    workShifts: {
      all: () => [...keys.hr.all, 'work-shifts'] as const,
      list: (params?: FetchWorkShiftsParams) =>
        [...keys.hr.workShifts.all(), 'list', params] as const,
      byId: (id: number | undefined) => [...keys.hr.workShifts.all(), id] as const,
    },
    planningConfig: {
      all: () => [...keys.hr.all, 'planning-config'] as const,
    },
    lastOptimization: (params?: { dateStart: string; dateEnd: string }) =>
      [...keys.hr.all, 'last-optimization', params] as const,
    transportSegments: (params?: FetchTransportSegmentsParams) =>
      [...keys.hr.all, 'transport-segments', params] as const,
    smsTracking: {
      all: () => [...keys.hr.all, 'sms-tracking'] as const,
      list: (params: FetchSmsTrackingListParams) =>
        [...keys.hr.smsTracking.all(), 'list', params] as const,
      stats: (params: FetchSmsTrackingStatsParams) =>
        [...keys.hr.smsTracking.all(), 'stats', params] as const,
    },
  },
  platformStocks: {
    all: ['platform-stocks'] as const,
    lists: () => [...keys.platformStocks.all, 'list'] as const,
    list: (params?: FetchPlatformStocksParams) => [...keys.platformStocks.lists(), params] as const,
    logs: (id: number) => [...keys.platformStocks.all, id, 'logs'] as const,
    allLogs: (id: number) => [...keys.platformStocks.all, id, 'logs', 'all'] as const,
    distinctProducts: (params?: FetchDistinctPlatformStockProductsParams) =>
      [...keys.platformStocks.all, 'distinct-products', params] as const,
    distinctLots: (params?: FetchDistinctPlatformStockLotsParams) =>
      [...keys.platformStocks.all, 'distinct-lots', params] as const,
  },
}
